import { ApiMethodsPropType, fetchWithHeaders, getErrorFromFetchWithHeaders, reportErrorToFaro } from '@sendible/common';
import { CampaignsUrls } from '../../constants/urls';
import { useUrlWithCredentials } from '../../data-layer/useUrlWithCredentials';

export type CampaignsDataForApi = {
  name?: string;
  startDate?: string;
  endDate?: string;
  status: string;
  paramId?: string;
};

export const useCampaignsModel = () => {
  const getUrlWithCredentials = useUrlWithCredentials();

  const fetchApiHandler = async <T = unknown>(url: string, method: ApiMethodsPropType = 'GET'): Promise<T> => {
    return fetchWithHeaders({
      method,
      url,
      headers: { 'Content-Type': 'application/json' },
    }).then((response) => {
      const typedResponse = response as { result?: T } | T;

      return (typeof typedResponse === 'object' && typedResponse !== null && 'result' in typedResponse ? typedResponse.result : typedResponse) as T;
    });
  };

  const getCampaigns = async (): Promise<CampaignsDataFromApi[]> => {
    try {
      const url = getUrlWithCredentials(CampaignsUrls.campaigns);

      return await fetchApiHandler(url);
    } catch (error: unknown) {
      reportErrorToFaro(error, 'CampaignsModel getCampaigns');

      throw Error(getErrorFromFetchWithHeaders(error));
    }
  };

  const saveCampaign = async (campaignObject: CampaignsDataForApi, method: ApiMethodsPropType = 'POST'): Promise<{ id: number }> => {
    const url = `${getUrlWithCredentials(CampaignsUrls.campaign)}&${new URLSearchParams(campaignObject).toString()}`;

    return fetchApiHandler<{ id: number }>(url, method);
  };

  const deleteCampaign = async (campaignId: number): Promise<void> => {
    const url = `${getUrlWithCredentials(CampaignsUrls.campaign)}&campaignId=${campaignId}`;

    await fetchApiHandler(url, 'DELETE');
  };

  const getCampaignOverview = async (campaignId: number): Promise<CampaignsOverview> => {
    try {
      const url = `${getUrlWithCredentials(CampaignsUrls.overview)}&campaignId=${campaignId}`;

      return await fetchApiHandler<CampaignsOverview>(url);
    } catch (error: unknown) {
      reportErrorToFaro(error, 'CampaignsModel getCampaignOverview');

      throw Error(getErrorFromFetchWithHeaders(error));
    }
  };

  return {
    getCampaigns,
    saveCampaign,
    deleteCampaign,
    getCampaignOverview,
  };
};
